import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, X } from "lucide-react";
import { JerseyNumber } from "../../components/rink/JerseyNumber";
import { IconButton } from "../../components/ui/IconButton";
import { Switch } from "../../components/ui/Switch";
import { useI18n } from "../../i18n/I18nProvider";
import type { Person } from "../../lib/api-types";
import { PersonThumb } from "../roster/PersonThumb";
import { HotkeyRecorder } from "./HotkeyRecorder";
import { lineupSong } from "./lineup-song";
import type { ConflictKind, DraftSlot } from "./slot-state";

export interface SlotRowProps {
  workspaceId: string;
  slot: DraftSlot;
  person: Person | undefined;
  conflict: ConflictKind | undefined;
  disabled: boolean;
  onToggleActive: () => void;
  onSetHotkey: (hotkey: string | null) => void;
  onRemove: () => void;
}

/** Fixed-width, right-aligned number column so single and two-digit numbers line up on every row. */
const NUMBER_COLUMN = "flex w-16 shrink-0 justify-end";

/** A sortable lineup row: drag handle, number, photo, name and song, hotkey, active switch, remove. */
export function SlotRow(props: SlotRowProps) {
  const { t } = useI18n();
  const { slot, person, conflict, disabled, workspaceId } = props;
  const name = person?.name ?? "—";
  const sortable = useSortable({ id: slot.personId, disabled });
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
  };

  return (
    <li
      ref={sortable.setNodeRef}
      style={style}
      className={`flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-hairline py-3 ${slot.active ? "" : "opacity-80"}`}
      data-testid="slot-row"
    >
      <IconButton
        ref={sortable.setActivatorNodeRef}
        {...sortable.attributes}
        {...sortable.listeners}
        disabled={disabled}
        label={t("editor.dragHandle")}
        icon={<GripVertical aria-hidden="true" className="size-[18px]" strokeWidth={1.75} />}
        className="cursor-grab touch-none"
      />
      <div className={NUMBER_COLUMN}>
        <JerseyNumber value={person?.number ?? "—"} size="md" />
      </div>
      <PersonThumb workspaceId={workspaceId} mediaId={person?.photoMediaId ?? null} name={name} />
      <div className="min-w-0 flex-1 basis-40">
        <p className="truncate text-[1.0625rem] font-semibold text-ink">{name}</p>
        <SongLine person={person} />
      </div>
      <HotkeyRecorder
        name={name}
        value={slot.hotkey}
        disabled={disabled}
        onChange={props.onSetHotkey}
      />
      <div className="flex items-center gap-2">
        <Switch
          compact
          checked={slot.active}
          disabled={disabled}
          label={`${t("editor.activeLabel")}: ${name}`}
          onChange={() => props.onToggleActive()}
        />
        <span className="text-note font-semibold text-ink-2">
          {slot.active ? t("common.active") : t("common.inactive")}
        </span>
      </div>
      <IconButton
        label={t("editor.removeFromLineup", { name })}
        icon={<X aria-hidden="true" className="size-[18px]" strokeWidth={1.75} />}
        disabled={disabled}
        onClick={props.onRemove}
      />
      {conflict ? (
        <p role="alert" className="w-full text-note font-medium text-red">
          {t(
            conflict === "reserved" ? "editor.hotkeyReservedLocal" : "editor.hotkeyDuplicateLocal",
          )}
        </p>
      ) : null}
    </li>
  );
}

function SongLine({ person }: { person: Person | undefined }) {
  const { t } = useI18n();
  const song = lineupSong(person);
  if (song.kind === "none") {
    return <p className="text-note text-ink-3">{t("roster.noSong")}</p>;
  }
  return (
    <p className="truncate text-note text-ink-2">
      {song.label} <span className="tabular-nums text-ink-3">{song.region}</span>
    </p>
  );
}
